<template>
  <div
    class="quest-item"
    :class="{
      'active-quest': isActive,
      'ready-quest': isReady,
      'drop-target': !isActive,
      'drag-over': isDragOver && !isActive,
    }"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <!-- Header / Collapsed View -->
    <div class="quest-header" @click="toggleExpand">
      <div class="quest-info">
        <h3 class="quest-title">{{ quest.title }}</h3>
        <div class="quest-meta">
          <span class="level-badge">Lvl {{ quest.recommended_level }}</span>
          <span class="duration-badge" v-if="!isActive">⏱️ {{ durationMinutes }}m</span>
          <span class="timer-badge desktop-meta-status" v-else-if="!isReady">⏳ {{ timeRemaining }}</span>
          <span class="ready-badge desktop-meta-status" v-else>✅ READY</span>
          <span v-if="chainLabel" class="chain-chip">{{ chainLabel }}</span>
        </div>
        <div v-if="!isActive && (assignedHero || hasAssignedTroops)" class="assigned-chip">
          <HeroTagChip
            v-if="assignedHero"
            :name="heroDisplayName(assignedHero)"
            :meta="`Lv ${assignedHero.level || 1}`"
            :image="assignedHeroCard?.image || ''"
            :image-position="assignedHeroCard?.image_position"
            :rarity="assignedHeroCard?.rarity || 'common'"
            removable
            @remove="clearAssignedHero"
          />
          <div v-if="hasAssignedTroops" class="troop-chip-row">
            <span
              v-for="chip in assignedTroopChips"
              :key="chip.slug"
              class="troop-chip"
            >
              <i :class="chip.icon" :style="{ color: chip.color }"></i>
              {{ chip.qty }} {{ chip.name }}
            </span>
          </div>
        </div>
      </div>
      <div class="header-right">
        <span class="chance-preview" :class="chanceColorClass" v-if="!isActive && totalChance != null">
          {{ totalChance }}% Success
        </span>
        <span class="chance-preview chance-empty" v-else-if="!isActive">
          {{ emptyAssignHint }}
        </span>
        <span class="header-status ready-badge" v-if="isReady">READY</span>
        <span class="header-status expedition-badge" v-else-if="isActive">EXPEDITION</span>
        <button class="expand-btn" type="button">
          {{ isExpanded ? '▲' : '▼' }}
        </button>
      </div>
    </div>

    <!-- Expanded View -->
    <div v-if="isExpanded" class="quest-details">
      <div class="quest-image-container" v-if="quest.image_url">
        <img :src="resolveImage(quest.image_url)" :alt="quest.title" class="quest-image" />
      </div>
      <div class="quest-content">
        
        <div class="quest-description-block">
          <p class="description">{{ quest.description }}</p>
          
          <div class="rewards-section">
            <h4>Rewards</h4>
            <div class="rewards-list">
              <template v-for="(entry, index) in rewardEntries" :key="index">
                <span
                  v-if="entry.kind === 'pack'"
                  class="reward-tag pack-tag"
                >
                  <img
                    v-if="packImage(entry.pack_slug)"
                    :src="packImage(entry.pack_slug)"
                    alt=""
                    class="pack-icon"
                  />
                  <span v-else>📦</span>
                  {{ entry.amount > 1 ? `${entry.amount}× ` : "" }}{{ packName(entry.pack_slug) }}
                </span>
                <span
                  v-else-if="entry.kind === 'item'"
                  class="reward-tag item-tag"
                >
                  ⚔️ {{ entry.amount }}
                </span>
                <span v-else class="reward-tag" :class="entry.type">
                  {{ getRewardIcon(entry.type) }} {{ entry.amount }} {{ formatKey(entry.type) }}
                </span>
              </template>
            </div>
          </div>

          <div class="mechanics-section" v-if="!isActive">
            <h4>Expedition Planning</h4>
            
            <!-- Success Chance Display -->
            <div v-if="totalChance != null" class="chance-display" :class="chanceColorClass">
              <div class="chance-label">Success Chance</div>
              <div class="chance-value">{{ totalChance }}%</div>
              <div class="chance-risk">{{ riskLabel }}</div>
            </div>
            <div v-else class="chance-display chance-empty">
              <div class="chance-label">Success Chance</div>
              <div class="chance-value">—</div>
              <div class="chance-risk">{{ emptyAssignHint }} to preview chance</div>
            </div>
            <p v-if="levelGateBanner" class="range-warning">
              {{ levelGateBanner }}
            </p>
            <p v-else-if="assignedHeroOutOfRange" class="range-warning">
              This hero is outside the recommended level range (±{{ levelRange }}).
            </p>

            <div class="hero-assign-slot" @click.stop="openHeroPicker">
              <HeroTagChip
                v-if="assignedHero"
                block
                :name="heroDisplayName(assignedHero)"
                :meta="`Lv ${assignedHero.level || 1}`"
                :image="assignedHeroCard?.image || ''"
                :image-position="assignedHeroCard?.image_position"
                :rarity="assignedHeroCard?.rarity || 'common'"
                removable
                @remove="clearAssignedHero"
              />
              <div v-else class="hero-assign-empty">
                + Tap to Assign Hero
              </div>
            </div>

            <QuestTroopAssign
              v-if="isMobile && allowsTroops && questSlug"
              :quest-slug="questSlug"
            />

            <div class="resources-input">
              <!-- Gold Input -->
              <div class="input-group">
                <div class="label-row">
                  <span>💰 Gold Investment</span>
                  <span class="cost-val" :class="{ 'red': !hasEnoughGold }">
                    Total: {{ totalGoldCost }} / {{ currentResources?.gold || 0 }}
                  </span>
                </div>
                 <div class="slider-row">
                    <span class="min-badge">Min: {{ quest.min_gold_cost || 0 }}</span>
                    <input 
                      type="range" 
                      min="0" 
                      :max="maxAddedGold" 
                      step="10" 
                      v-model.number="addedGold"
                      :disabled="!canAffordBase"
                    />
                    <span class="added-val">+{{ addedGold }}</span>
                 </div>
                 <div class="conversion-info" v-if="quest.gold_cost_per_percent > 0">
                   +1% per {{ quest.gold_cost_per_percent }} Gold
                 </div>
              </div>
            </div>

            <button 
              class="prepare-btn" 
              :disabled="!canStart"
              @click.stop="prepareExpedition"
            >
              Prepare Expedition
            </button>
          </div>

          <!-- Active Quest State -->
          <div class="active-section" v-else>
            <h4>Mission In Progress</h4>
            <div class="status-display">
              <div class="status-label">Status</div>
              <div class="status-value" v-if="!isReady">En Route...</div>
              <div class="status-value ready" v-else>Mission Complete!</div>
            </div>
            
            <button 
              class="claim-btn" 
              :class="{ 'ready': isReady }"
              :disabled="!isReady"
              @click.stop="claimReward"
            >
              {{ isReady ? '🎁 Reveal Outcome' : `⏳ ${timeRemaining}` }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <QuestHeroPicker
      :is-visible="heroPickerOpen"
      :quest-slug="questSlug"
      @close="heroPickerOpen = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useGameStore } from '../store/game';
import {
  calculateQuestSuccessChance,
  maxGoldForChanceCap,
  troopBoostFromAssignment,
  questAllowsTroops,
  assignedTroopCount,
} from '../utils/questChance';
import { flattenQuestRewards, packBySlug } from '../utils/questRewards';
import { UNIT_SLUGS, unitDisplayMap } from '../utils/unitDisplay';
import HeroTagChip from './HeroTagChip.vue';
import QuestTroopAssign from './QuestTroopAssign.vue';
import QuestHeroPicker from './QuestHeroPicker.vue';

const props = defineProps({
  quest: {
    type: Object,
    required: true
  },
  playerLevel: {
    type: Number,
    required: true,
    default: 1
  }
});

const emit = defineEmits(['prepare-expedition']);

const isExpanded = ref(false);
const addedGold = ref(0);
const now = ref(new Date());
const heroPickerOpen = ref(false);
const isMobile = ref(false);
const MOBILE_MAX_WIDTH = 768;
let timerInterval = null;

const gameStore = useGameStore();
const isDragOver = ref(false);
const levelRange = 3;

const questSlug = computed(() => props.quest.slug || props.quest.quest_slug);

const chainLabel = computed(() => {
  const name = props.quest.chain_name;
  const current = props.quest.step_current;
  const total = props.quest.step_total;
  if (!name || !current || !total) return "";
  return `Part ${current} of ${total}: ${name}`;
});

const rewardEntries = computed(() => flattenQuestRewards(props.quest.rewards));

const packName = (slug) => packBySlug(slug)?.name || slug || "Pack";
const packImage = (slug) => packBySlug(slug)?.image || "";

const assignment = computed(() =>
  gameStore.assignmentForQuest(questSlug.value)
);

const assignedHero = computed(() => {
  const id = assignment.value.assigned_hero_id;
  if (!id) return null;
  return gameStore.playerHeroes.find((h) => h.id === id) || null;
});

const assignedHeroCard = computed(() =>
  assignedHero.value
    ? gameStore.socketedHeroCard(assignedHero.value.id)
    : null
);

const heroLevel = computed(() => assignedHero.value?.level || null);

const troopBoost = computed(() =>
  troopBoostFromAssignment(assignment.value.assigned_troops)
);

const heroDisplayName = (hero) => {
  if (!hero) return "Hero";
  return hero.name || `Hero #${hero.id}`;
};

const assignedHeroOutOfRange = computed(() => {
  if (heroLevel.value == null) return false;
  const recommended = props.quest.recommended_level ?? 1;
  return Math.abs(heroLevel.value - recommended) > levelRange;
});

const allowsTroops = computed(() => questAllowsTroops(props.quest));

const hasAssignedTroops = computed(
  () => assignedTroopCount(assignment.value.assigned_troops) > 0
);

const assignedTroopChips = computed(() => {
  const troops = assignment.value.assigned_troops || {};
  return UNIT_SLUGS.filter((slug) => (Number(troops[slug]) || 0) > 0).map((slug) => {
    const display = unitDisplayMap[slug] || { name: slug, icon: "fas fa-users", color: "#d8d0c0" };
    return {
      slug,
      qty: Number(troops[slug]) || 0,
      name: display.name,
      icon: display.icon,
      color: display.color,
    };
  });
});

const emptyAssignHint = computed(() => {
  if (isMobile.value) {
    return allowsTroops.value ? "Tap to assign a hero or troops" : "Tap to assign a hero";
  }
  return allowsTroops.value ? "Drop a hero or assign troops" : "Drop a hero";
});

const primaryHeroOutOfRange = computed(() => {
  const recommended = props.quest.recommended_level ?? 1;
  return Math.abs((props.playerLevel || 1) - recommended) > levelRange;
});

const levelGateBanner = computed(() => {
  if (isActive.value) return "";
  const recommended = props.quest.recommended_level ?? 1;
  if (assignedHero.value && assignedHeroOutOfRange.value) {
    return `This hero is outside the recommended level range (±${levelRange}).`;
  }
  if (!assignedHero.value && primaryHeroOutOfRange.value) {
    return `Requires a hero near level ${recommended} (±${levelRange}) before you can start.`;
  }
  return "";
});

const parseDropPayload = (event) => {
  try {
    const raw = event.dataTransfer.getData("text/plain");
    if (!raw) return null;
    const payload = JSON.parse(raw);
    if (!payload || typeof payload !== "object") return null;
    if (payload.type !== "hero" && payload.type !== "troop") return null;
    return payload;
  } catch {
    return null;
  }
};

const onDragOver = (event) => {
  if (isActive.value) return;
  event.preventDefault();
  event.dataTransfer.dropEffect = "move";
  isDragOver.value = true;
};

const onDragLeave = (event) => {
  if (event.currentTarget.contains(event.relatedTarget)) return;
  isDragOver.value = false;
};

const onDrop = (event) => {
  event.preventDefault();
  isDragOver.value = false;
  if (isActive.value) return;
  const payload = parseDropPayload(event);
  if (!payload) return;
  gameStore.assignUnitToQuest(questSlug.value, payload);
};

const clearAssignedHero = () => {
  gameStore.clearQuestAssignment(questSlug.value, "hero");
};

const openHeroPicker = () => {
  if (isActive.value) return;
  heroPickerOpen.value = true;
};

function syncMobile() {
  isMobile.value = window.innerWidth <= MOBILE_MAX_WIDTH;
}

// Active Quest Detection
const isActive = computed(() => {
  return props.quest.status === 'in_progress' && props.quest.end_time;
});

const isReady = computed(() => {
  if (!isActive.value) return false;
  const endTime = new Date(props.quest.end_time);
  return now.value >= endTime;
});

const timeRemaining = computed(() => {
  if (!isActive.value) return '';
  const endTime = new Date(props.quest.end_time);
  const diff = endTime - now.value;
  if (diff <= 0) return 'Ready!';
  
  const minutes = Math.floor(diff / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
});

// Timer lifecycle
onMounted(() => {
  syncMobile();
  window.addEventListener("resize", syncMobile);
  timerInterval = setInterval(() => {
    now.value = new Date();
  }, 1000);
});

onUnmounted(() => {
  window.removeEventListener("resize", syncMobile);
  if (timerInterval) clearInterval(timerInterval);
  if (gameStore.focusedQuestSlug === questSlug.value) {
    gameStore.setFocusedQuestSlug(null);
  }
});

const durationMinutes = computed(() => {
  return Math.floor((props.quest.duration_seconds || 0) / 60);
});

const currentResources = computed(() => gameStore.spendableResources || {});

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
  if (isExpanded.value) {
    gameStore.setFocusedQuestSlug(questSlug.value);
  } else if (gameStore.focusedQuestSlug === questSlug.value) {
    gameStore.setFocusedQuestSlug(null);
  }
};

// --- Rewards Logic ---
const getRewardIcon = (key) => {
  const map = {
    gold: '💰',
    xp: '✨',
    wood: '🪵',
    stone: '🪨',
    food: '🍎',
    iron: '⛓️',
    gems: '💎',
    resource: '📦',
    item: '⚔️'
  };
  return map[key] || '🎁';
};

const formatKey = (key) => {
  if (key === 'xp' || key === 'gold') return ''; // already clear or unitless
  return key.charAt(0).toUpperCase() + key.slice(1);
};

// --- Cost Logic ---
const totalGoldCost = computed(() => (props.quest.min_gold_cost || 0) + addedGold.value);

const hasEnoughGold = computed(() => (currentResources.value?.gold || 0) >= totalGoldCost.value);
const canAffordBase = computed(() => {
    const baseGold = props.quest.min_gold_cost || 0;
    return (currentResources.value?.gold || 0) >= baseGold;
});

const hasAssignedHero = computed(() => !!assignedHero.value);
const partyReady = computed(() => {
  if (hasAssignedHero.value) return !assignedHeroOutOfRange.value;
  return allowsTroops.value && hasAssignedTroops.value;
});
const canStart = computed(
  () => hasEnoughGold.value && partyReady.value
);

// --- Chance Logic ---
const totalChance = computed(() => {
  if (heroLevel.value == null && !hasAssignedTroops.value) return null;
  return calculateQuestSuccessChance({
    heroLevel: heroLevel.value,
    questLevel: props.quest.recommended_level ?? 1,
    addedGold: addedGold.value,
    goldCostPerPercent: props.quest.gold_cost_per_percent || 0,
    troopBoost: troopBoost.value,
  });
});

const maxAddedGold = computed(() => {
    if (heroLevel.value == null && !hasAssignedTroops.value) return 0;
    const chanceMax = maxGoldForChanceCap({
      heroLevel: heroLevel.value,
      questLevel: props.quest.recommended_level ?? 1,
      goldCostPerPercent: props.quest.gold_cost_per_percent || 0,
      troopBoost: troopBoost.value,
    });
    const available = Math.max(
      0,
      (currentResources.value?.gold || 0) - (props.quest.min_gold_cost || 0)
    );
    return Math.floor(Math.min(chanceMax, available));
});


// --- Details & Styling ---
const chanceColorClass = computed(() => {
  if (totalChance.value == null) return '';
  if (totalChance.value >= 80) return 'chance-high';
  if (totalChance.value >= 50) return 'chance-medium';
  return 'chance-low';
});

const riskLabel = computed(() => {
  if (totalChance.value == null) return '';
  if (totalChance.value >= 80) return 'Safe Bet';
  if (totalChance.value >= 50) return 'Risky';
  return 'Suicide Mission';
});


const resolveImage = (url) => {
  if (!url) return '';
  if (url.startsWith('/') || url.startsWith('http')) return url;
  return '/' + url;
};


const prepareExpedition = () => {
  emit('prepare-expedition', {
    questSlug: props.quest.slug,
    totalGold: totalGoldCost.value,
    assigned_hero_id: assignment.value.assigned_hero_id,
    assigned_troops: assignment.value.assigned_troops || {},
  });
};

const claimReward = async () => {
  if (!isReady.value) return;
  try {
    await gameStore.claimQuest(props.quest.id);
  } catch (error) {
    console.error('Failed to claim quest:', error);
  }
};
</script>

<style scoped>
.quest-item {
  width: 100%;
  max-width: 800px;
  margin: 0 auto 1rem;
  box-sizing: border-box;
  background: rgba(10, 15, 30, 0.95);
  border: 1px solid #00f0ff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 10px rgba(0, 240, 255, 0.1);
  transition: box-shadow 0.3s ease, border-color 0.3s ease;
  color: #e0e0e0;
  font-family: 'Rajdhani', sans-serif;
}

.quest-item.drop-target.drag-over {
  border-color: #a3be8c;
  box-shadow: 0 0 18px rgba(163, 190, 140, 0.45);
}

.quest-item:hover {
  box-shadow: 0 0 15px rgba(0, 240, 255, 0.3);
  border-color: #50ffff;
}

.quest-header {
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  background: linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0, 240, 255, 0.1) 100%);
}

.quest-title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.quest-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #88c0d0;
  align-items: center;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.chance-preview {
  font-weight: bold;
  font-size: 0.9rem;
}

.chance-preview.chance-empty {
  color: #8892b0;
  font-weight: 500;
  font-size: 0.8rem;
}

.assigned-chip {
  margin-top: 0.5rem;
}

.troop-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.troop-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(0, 240, 255, 0.12);
  border: 1px solid rgba(0, 240, 255, 0.35);
  font-size: 0.75rem;
  color: #d8e8f0;
}

.troop-chip i {
  font-size: 0.7rem;
}

.chain-chip {
  display: inline-block;
  margin-top: 0;
  padding: 0.2rem 0.55rem;
  border-radius: 4px;
  background: rgba(163, 190, 140, 0.15);
  border: 1px solid rgba(163, 190, 140, 0.45);
  color: #a3be8c;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.pack-tag {
  border-color: rgba(235, 203, 139, 0.45);
  background: rgba(235, 203, 139, 0.12);
}

.pack-icon {
  width: 16px;
  height: 16px;
  object-fit: cover;
  border-radius: 2px;
}

.range-warning {
  color: #bf616a;
  font-size: 0.85rem;
  margin: 0 0 1rem;
}

.hero-assign-slot {
  margin-bottom: 1rem;
  cursor: pointer;
}

.hero-assign-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: 0.75rem 1rem;
  border: 1px dashed rgba(0, 246, 255, 0.55);
  border-radius: 8px;
  color: #00f0ff;
  font-family: "Orbitron", sans-serif;
  font-size: 0.78rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: rgba(0, 246, 255, 0.04);
}

.chance-display.chance-empty {
  color: #8892b0;
  border-color: #444;
}

.expand-btn {
  background: none;
  border: none;
  color: #00f0ff;
  font-size: 1.2rem;
  cursor: pointer;
}

.quest-details {
  padding: 1rem;
  border-top: 1px solid rgba(0, 240, 255, 0.3);
  background: rgba(0, 5, 15, 0.6);
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.quest-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.quest-image-container {
  width: 100%;
  height: 250px;
  overflow: hidden;
  margin-bottom: 1rem;
}

.quest-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  border-radius: 4px;
  border: 1px solid #444;
  box-shadow: 0 4px 6px rgba(0,0,0,0.5);
}

.quest-description-block {
  flex: 1;
}

.description {
  font-style: italic;
  color: #ccc;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

/* Rewards */
.rewards-section {
  margin-bottom: 1.5rem;
  background: rgba(255, 255, 255, 0.03);
  padding: 0.8rem;
  border-radius: 4px;
  border-left: 3px solid #00f0ff;
}

.rewards-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.reward-tag {
  background: rgba(0, 240, 255, 0.1);
  border: 1px solid rgba(0, 240, 255, 0.3);
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #d8dee9;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

/* Mechanics / Gambling */
.mechanics-section {
  background: rgba(0, 0, 0, 0.4);
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.chance-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  background: rgba(0,0,0,0.5);
  border: 1px solid #333;
}

.chance-label { font-size: 0.9rem; text-transform: uppercase; color: #888; }
.chance-value { font-size: 1.8rem; font-weight: 800; }
.chance-risk { font-size: 1rem; font-weight: bold; text-transform: uppercase; letter-spacing: 1px;}

.chance-high { color: #a3be8c; border-color: #a3be8c; }
.chance-medium { color: #ebcb8b; border-color: #ebcb8b; }
.chance-medium .chance-value, .chance-medium .chance-risk, .chance-medium.chance-preview { color: #ebcb8b; }
.chance-low { color: #bf616a; border-color: #bf616a; }

.input-group {
  margin-bottom: 1rem;
}

.label-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  margin-bottom: 0.3rem;
  color: #a0a0a0;
}

.cost-val { font-weight: bold; color: #fff; }
.cost-val.red { color: #bf616a; }

.slider-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.min-badge {
  font-size: 0.75rem;
  background: #333;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  color: #aaa;
  min-width: 50px;
  text-align: center;
}

input[type="range"] {
  flex: 1;
  cursor: pointer;
  accent-color: #00f0ff;
}

.added-val {
  min-width: 40px;
  text-align: right;
  color: #00f0ff;
  font-family: monospace;
}

.conversion-info {
  font-size: 0.7rem;
  color: #666;
  margin-top: 0.2rem;
  margin-left: 60px; /* align with slider start roughly */
}

/* Button */
.prepare-btn {
  width: 100%;
  padding: 1rem;
  margin-top: 1rem;
  background: linear-gradient(45deg, #0077ff, #00f0ff);
  border: none;
  border-radius: 4px;
  color: #000;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.2s;
  text-shadow: 0 0 5px rgba(255,255,255,0.4);
}

.prepare-btn:disabled {
  background: #2a2a2a;
  color: #555;
  cursor: not-allowed;
  text-shadow: none;
  border: 1px solid #444;
}

.prepare-btn:not(:disabled):hover {
  filter: brightness(1.2);
  box-shadow: 0 0 20px rgba(0, 240, 255, 0.4);
  transform: translateY(-1px);
}

/* Active Quest Styles */
.active-quest {
  border-color: #ebcb8b;
}

.ready-quest {
  border-color: #a3be8c;
  animation: readyPulse 2s infinite;
}

@keyframes readyPulse {
  0%, 100% { box-shadow: 0 0 10px rgba(163, 190, 140, 0.3); }
  50% { box-shadow: 0 0 20px rgba(163, 190, 140, 0.6); }
}

.timer-badge, .ready-badge {
  font-weight: bold;
  padding: 0.2rem 0.5rem;
  border-radius: 3px;
  font-size: 0.85rem;
}

.timer-badge {
  background: rgba(235, 203, 139, 0.2);
  color: #ebcb8b;
}

.ready-badge {
  background: rgba(163, 190, 140, 0.2);
  color: #a3be8c;
  animation: pulse 1s infinite;
}

/* Active Section */
.active-section {
  background: rgba(0, 0, 0, 0.4);
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.status-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  background: rgba(0,0,0,0.5);
  border: 1px solid #333;
}

.status-label {
  font-size: 0.9rem;
  text-transform: uppercase;
  color: #888;
}

.status-value {
  font-size: 1.2rem;
  font-weight: 700;
  color: #ebcb8b;
}

.status-value.ready {
  color: #a3be8c;
  animation: pulse 1.5s infinite;
}

/* Claim Button */
.claim-btn {
  width: 100%;
  padding: 1rem;
  margin-top: 1rem;
  background: #2a2a2a;
  border: 1px solid #444;
  border-radius: 4px;
  color: #888;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: not-allowed;
  transition: all 0.3s;
  font-family: 'Orbitron', sans-serif;
}

.claim-btn.ready {
  background: linear-gradient(45deg, #88c070, #a3be8c);
  border: none;
  color: #000;
  cursor: pointer;
  text-shadow: 0 0 5px rgba(255,255,255,0.4);
  animation: claimPulse 2s infinite;
}

.claim-btn.ready:hover {
  filter: brightness(1.2);
  box-shadow: 0 0 25px rgba(163, 190, 140, 0.6);
  transform: translateY(-2px) scale(1.02);
}

@keyframes claimPulse {
  0%, 100% { box-shadow: 0 0 15px rgba(163, 190, 140, 0.4); }
  50% { box-shadow: 0 0 30px rgba(163, 190, 140, 0.7); }
}

.header-status {
  display: none;
}

@media (max-width: 768px) {
  .quest-header {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-areas:
      "title title"
      "meta right"
      "assigned assigned";
    align-items: center;
    gap: 0;
  }

  .quest-info {
    display: contents;
  }

  .quest-title {
    grid-area: title;
    font-size: 1rem;
    font-weight: 700;
    flex: 1;
    white-space: normal;
    line-height: 1.3;
  }

  .quest-meta {
    grid-area: meta;
    margin-top: 6px;
    gap: 0.4rem 0.65rem;
    font-size: 0.8rem;
  }

  .header-right {
    grid-area: right;
    margin-top: 6px;
    gap: 0.4rem;
  }

  .assigned-chip {
    grid-area: assigned;
  }

  .chance-preview,
  .chance-preview.chance-empty {
    display: none;
  }

  .desktop-meta-status {
    display: none;
  }

  .header-status {
    display: inline-flex;
    align-items: center;
    font-weight: bold;
    padding: 0.2rem 0.5rem;
    border-radius: 3px;
    font-size: 0.75rem;
    letter-spacing: 0.04em;
  }

  .header-status.expedition-badge {
    background: rgba(235, 203, 139, 0.2);
    color: #ebcb8b;
  }

  .active-quest .expand-btn,
  .ready-quest .expand-btn {
    display: none;
  }
}

</style>
